import { useQuery } from "@apollo/react-hooks";
import { ApolloError, ApolloQueryResult } from "@apollo/client";

import gql from "graphql-tag";
import {
  Homepage,
  ProductLabelFilter,
  CarouselRecommendTypeInput,
  CatalogProduct,
  ConstructorTrackingDataInput,
} from "src/types/graphql.d";
import { useState, useEffect, useMemo } from "react";
import { useFeatureFlags } from "./useFeatureFlags";
import { useAreFeatureFlagsResolved } from "src/hooks/stores/use-feature-flags-store";
import { useAuth } from "src/hooks/useAuth";
import { getConstructorTrackingData } from "src/utils/constructorTrackingData";
import { getHomepageRecommendationsConfig } from "src/utils/homepageRecommendationsFlag";

interface HomepageData {
  homepage: Homepage | undefined;
}
interface HomepageVars {
  gender: string;
  limit?: number;
  labels?: ProductLabelFilter[];
  carousel_recommend_type?: CarouselRecommendTypeInput;
}
interface HomepageHook {
  refetch: () => Promise<ApolloQueryResult<HomepageData>>;
  data: HomepageData | undefined;
  loading: boolean;
  error: ApolloError | undefined;
}

// The product fields a homepage product carousel renders.
const carouselProductFragment = gql`
  fragment carouselProduct on CatalogProduct {
    __typename
    id
    name
    thumbnail
    price
    regular_price
    slug
    brand {
      id
      name
      brand_sale_type
    }
    currency {
      code
    }
    translated_category_ids
    translated_category_names
    product_discount
    product_kids_gender
    product_shop_type
    available_sizes
    labels {
      value
    }
    gate {
      level
    }
  }
`;

const GET_NEW_IN_QUERY = `query homepage(
    $gender: String!
    $limit: Int!
    $labels: [ProductLabelFilter!]!
    $carousel_recommend_type: CarouselRecommendTypeInput
  ) {
    homepage(gender: $gender) {
      productBlocks(
        limit: $limit
        labels: $labels
        carousel_recommend_type: $carousel_recommend_type
      ) {
        type
        position
        data {
          is_enabled
          title
          url
          personalised
          products {
            ...carouselProduct
          }
          response_source_type
          pod_id
          result_id
          strategy_id
        }
      }
    }
  }`;

const GET_NEW_IN = gql`
  ${GET_NEW_IN_QUERY}
  ${carouselProductFragment}
`;

const GET_RECOMMENDATION_CAROUSEL = gql`
  query recommendationCarousel(
    $pod_id: String!
    $shop_type: ProductGender!
    $limit: Int!
    $constructor_tracking_data: ConstructorTrackingDataInput
  ) {
    recommendationCarousel(
      pod_id: $pod_id
      shop_type: $shop_type
      limit: $limit
      constructor_tracking_data: $constructor_tracking_data
    ) {
      products {
        ...carouselProduct
      }
      result_id
      pod_id
      strategy_id
    }
  }
  ${carouselProductFragment}
`;

interface RecommendationCarouselData {
  recommendationCarousel: {
    products: CatalogProduct[];
    result_id?: string | null;
    pod_id?: string | null;
    strategy_id?: string | null;
  } | null;
}
interface RecommendationCarouselVars {
  pod_id: string;
  shop_type: string;
  limit: number;
  constructor_tracking_data: ConstructorTrackingDataInput;
}
export interface HomepageRecommendations {
  title: string;
  products: CatalogProduct[];
  podId?: string;
  resultId?: string;
  strategyId?: string;
}

const GET_SHOPS_HOMEPAGE_QUERY = `query homepage($gender: String!) {
    homepage(gender: $gender) {
      Shops {
        type
        position
        url
        title
        data {
          id
          name
          discount
          subtitle
          slug
          genders
          is_coming_soon
          logo
          overlay_logo
          logo_svg
          image
          images {
            men {
              device
              url
            }
            women {
              device
              url
            }
            kids {
              device
              url
            }
          }
          cover_image
          user_wishlist_item_id
          labels {
            type
            value
            color
            promotion {
              promo_code
              promo_message
              promo_message_url
            }
            priority
            text_color
          }
          gate {
            level
          }
        }
      }
    }
  }`;

const GET_SHOPS_HOMEPAGE = gql`
  ${GET_SHOPS_HOMEPAGE_QUERY}
`;

const GET_HOMEPAGE_QUERY = `query homepage($gender: String!) {
    homepage(gender: $gender) {
      PromoBanners {
        type
        position
        data {
          title
          description
          link
          desktop_image
          mobile_image
          name
        }
      }

      PromotionBanners {
        type
        position
        data {
          title
          description
          banner_link
          tablet_image
          tablet_image_width
          tablet_image_height
          mobile_image
          mobile_image_width
          mobile_image_height
          desktop_image
          button_title
          button_colour
          condition_text
          background_colour
          text_colour
          type
          promo_code
          promo_code_colour
          start_date
          end_date
          hide_cta_button
          gated
          disable_bg_color_desktop
          text_alignment
          title_color
        }
      }

      ImageBanners {
        type
        position
        data {
          block_uuid
          link
          title
          subtitle
          subtitle_color
          image_mobile
          image_desktop
          title_color
          platform
          gated
          display_type
          start_date
          end_date
        }
      }

      Departments {
        type
        position
        data {
          department_title
          department_subtitle
          department_slug
          brands {
            id
            name
            discount
            subtitle
            slug
            image
            overlay_logo
            logo_svg
            labels {
              type
              value
              color
              promotion {
                promo_code
                promo_message
              }
              priority
              text_color
            }
            gate {
              level
            }
            user_wishlist_item_id
          }
        }
      }

      Categories {
        type
        position
        data {
          id
          title
          subtitle
          banner_image
          banner_image_mobile
          url
          slug
          products {
            id
            user_wishlist_item_id
            brand {
              id
              name
              slug
              designer_brand
            }
            brand_sale_type
            currency {
              code
            }
            slug
            price_float
            regular_price_float
            thumbnail
            name
            translated_category_ids
            translated_category_names
            product_discount
            product_kids_gender
            product_shop_type
            sizes_available
            labels {
              value
            }
            gate {
              level
            }
          }
        }
      }

      ActionBlocks {
        type
        position
        data {
          title
          subtitle
          description
          code
          image
          button_title
          button_link
        }
      }

      InspirationalSections {
        type
        position
        data {
          title
          subtitle
          link
          image
          linkCTA
          gated
        }
      }

      PopularCategories {
        type
        position
        gated
        data {
          id
          slug
          title
          thumbnail
          url
        }
      }

      MarketingBlocks {
        type
        position
        data {
          position
          image_banners {
            block_uuid
            link
            title
            title_color
            subtitle
            subtitle_color
            image_mobile
            image_desktop
            platform
            gated
            display_type
            start_date
            end_date
          }
        }
      }
    }
  }`;

const GET_HOMEPAGE = gql`
  ${GET_HOMEPAGE_QUERY}
`;

type Cache =
  | "cache-first"
  | "network-only"
  | "cache-only"
  | "no-cache"
  | "standby"
  | "cache-and-network"
  | undefined;

const useHomepage = ({
  gender,
}: HomepageVars): Omit<HomepageHook, "refetch"> => {
  /**
   * Pupose of changin fetchPolicy on the client:
   * first render will be ssr garantee, once on the client the fetchPolicy will change
   * Why ?
   * there's a bug on the home page cache, the apollo cache update fields that are not supposed
   * to be updated when switching from women to kids section then back to women;
   * once on kids section the top banner image changes to kids image if both have same brand id
   * if the first render uses fetch on the client, the top banner render will be delayed and flicker
   */
  const [fetchPolicy, setFetchPolicy] = useState<Cache>(undefined);
  useEffect(() => {
    setFetchPolicy("cache-and-network");
  }, []);

  const { loading, error, data } = useQuery<HomepageData, HomepageVars>(
    GET_HOMEPAGE,
    {
      variables: {
        gender,
      },
      fetchPolicy,
      skip: !gender,
    }
  );

  return { loading, error, data };
};

const useNewInHomepage = ({
  gender,
  limit,
  labels,
  carousel_recommend_type,
}: HomepageVars): HomepageHook => {
  const { homepageNewInCarouselRecommender, recommendationEngineRouting } =
    useFeatureFlags();
  // The query carries the shopper's Eppo variants, so it waits for them rather
  // than going out with the defaults (which route every shopper to Constructor).
  const areFeatureFlagsResolved = useAreFeatureFlagsResolved();
  const variables = {
    gender,
    limit,
    labels,
    carousel_recommend_type: {
      homepage_new_in_carousel:
        typeof homepageNewInCarouselRecommender === "string"
          ? homepageNewInCarouselRecommender
          : null,
      recommend_routing:
        typeof recommendationEngineRouting === "string"
          ? recommendationEngineRouting
          : null,
      constructor_tracking_data: getConstructorTrackingData(),
    },
  };

  const { loading, error, data, refetch } = useQuery<
    HomepageData,
    HomepageVars
  >(GET_NEW_IN, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: !gender || !areFeatureFlagsResolved,
  });

  return {
    loading: loading || (!!gender && !areFeatureFlagsResolved),
    error,
    data,
    refetch,
  };
};

const useShopsHomepage = ({ gender }: HomepageVars): HomepageHook => {
  const { session } = useAuth();
  const { loading, error, data, refetch } = useQuery<
    HomepageData,
    HomepageVars
  >(GET_SHOPS_HOMEPAGE, {
    variables: {
      gender,
    },
    fetchPolicy: "cache-and-network",
    skip: !gender || !session,
  });

  return { loading, error, data, refetch };
};

/**
 * The homepage recommendations carousel this shopper's Eppo variant names for this store, or
 * null when the variant names none (no request is sent then) or the pod returns no products.
 * Shown only to shoppers whose recommendation-engine-routing assignment is "constructor": the
 * carousel's pod is always a Constructor pod, and a request from the "aws" group would reach
 * Constructor from shoppers that engine comparison counts as AWS.
 */
const useHomepageRecommendations = ({
  gender,
  limit,
}: {
  gender: string;
  limit: number;
}): HomepageRecommendations | null => {
  const { homepageRecommendations, recommendationEngineRouting } =
    useFeatureFlags();
  const config = useMemo(
    () =>
      recommendationEngineRouting === "constructor"
        ? getHomepageRecommendationsConfig(homepageRecommendations)
        : null,
    [homepageRecommendations, recommendationEngineRouting]
  );

  const { data } = useQuery<
    RecommendationCarouselData,
    RecommendationCarouselVars
  >(GET_RECOMMENDATION_CAROUSEL, {
    variables: {
      pod_id: config?.podId ?? "",
      shop_type: gender,
      limit,
      constructor_tracking_data: getConstructorTrackingData(),
    },
    fetchPolicy: "cache-and-network",
    skip: !config || !gender,
  });

  const carousel = data?.recommendationCarousel;
  if (!config || !carousel?.products.length) {
    return null;
  }

  return {
    title: config.title,
    products: carousel.products,
    podId: carousel.pod_id ?? undefined,
    resultId: carousel.result_id ?? undefined,
    strategyId: carousel.strategy_id ?? undefined,
  };
};

export {
  useHomepage,
  useNewInHomepage,
  useShopsHomepage,
  useHomepageRecommendations,
};
